// RDQ (reorder-quantity) calculator — replaces the Excel RDQ Order Calculator.
// Pure, dependency-free. US orders Full Sets only; CA orders Full Sets + NAA
// when mailing NAA. The "Test" row concept from MIC is deliberately never
// referenced here — only Active quantities are accepted as input.

export type RdqCountry = 'US' | 'CA';

export interface RdqInput {
  country: RdqCountry;
  noElec: boolean;
  mailingAsNaa: boolean;
  activeFullSets: number;
  activeNaa: number;
  activeElectronic: number;
}

export interface RdqLineItem {
  materialType: 'Full Set' | 'NAA';
  qtyToOrder: number;
}

export interface RdqResult {
  lines: RdqLineItem[];
  totalSets: number;
  notes: string[];
}

export function calculateRdq(input: RdqInput): RdqResult {
  const { country, noElec, mailingAsNaa, activeFullSets, activeNaa, activeElectronic } = input;
  const electronicFold = noElec ? activeElectronic : 0;
  const notes: string[] = [];
  if (noElec && activeElectronic > 0) {
    notes.push(`No Elec = Y — ${activeElectronic} electronic holder(s) folded into the Full Set count.`);
  }

  if (country === 'CA' && mailingAsNaa) {
    const fullSetQty = activeFullSets + electronicFold;
    notes.push('Canada, mailing as NAA — ordering both Full Sets and NAA.');
    return {
      lines: [
        { materialType: 'Full Set', qtyToOrder: fullSetQty },
        { materialType: 'NAA', qtyToOrder: activeNaa },
      ],
      totalSets: fullSetQty + activeNaa,
      notes,
    };
  }

  // US (always), or CA not mailing NAA — Full Sets only. NAA holders (when
  // present but not being mailed as NAA) fold into the Full Set count rather
  // than being silently dropped.
  const fullSetQty = activeFullSets + activeNaa + electronicFold;
  if (country === 'US') {
    notes.push('US — Full Sets only.');
  } else {
    notes.push('Canada, not mailing as NAA — Full Sets only; NAA holders folded in.');
  }
  return {
    lines: [{ materialType: 'Full Set', qtyToOrder: fullSetQty }],
    totalSets: fullSetQty,
    notes,
  };
}
